import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon, InfoIcon, ListIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { HolidayFormModal } from '../components/holidays/HolidayFormModal';
import { YearCalendar } from '../components/holidays/YearCalendar';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Modal } from '../components/ui/Modal';
import { PageHeader } from '../components/ui/PageHeader';
import { useHRStore } from '../hooks/useHRStore';
import type { Holiday, AppView } from '../types/selfService';
import { TODAY } from '../utils/format';
import { appliesToLabel, sortHolidays } from '../utils/holidays';
import { hrActions } from '../utils/hrStore';
import { policyName } from '../utils/timeOff';
const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');
export function Holidays({
  view
}: {
  view: AppView;
}) {
  const {
    holidays
  } = useHRStore();
  const isAdmin = view === 'admin';
  const [year, setYear] = useState(TODAY.getFullYear());
  const [editing, setEditing] = useState<Holiday | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<Holiday | null>(null);
  const yearHolidays = sortHolidays(holidays.filter(h => h.date.startsWith(String(year))));
  const years = Array.from(new Set(holidays.map(h => Number(h.date.slice(0, 4))))).sort();
  const minYear = Math.min(...years, TODAY.getFullYear());
  const maxYear = Math.max(...years, TODAY.getFullYear() + 1);
  const companyCount = yearHolidays.filter(h => h.kind === 'Company').length;
  const halfCount = yearHolidays.filter(h => h.halfDay).length;
  const openForm = (h: Holiday | null) => {
    setEditing(h);
    setFormOpen(true);
  };
  return <div data-id="element-1299" data-mp="1jxxhy2lph1e0">
      <PageHeader eyebrow="Company" title="Holidays" description="Madison Mortgage's holiday calendar. These dates are skipped automatically when time off is counted." actions={isAdmin && <Button variant="primary" icon={PlusIcon} onClick={() => openForm(null)} data-id="element-1301" data-mp="1sxtzlk9ogzzk">
              Add holiday
            </Button>} data-id="element-1300" data-mp="1jy78psekrckr" />

      <div className="mb-6 flex flex-wrap items-center gap-3" data-id="element-1302" data-mp="22ooz3qbxpw2y">
        <div className="inline-flex items-center gap-1 rounded-2xl bg-white p-1.5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-1303" data-mp="22o5iqe1xnfr1">
          <button type="button" onClick={() => setYear(y => y - 1)} disabled={year <= minYear} aria-label="Previous year" className="flex h-8 w-8 items-center justify-center rounded-lg text-navy transition hover:bg-canvas disabled:opacity-30" data-id="element-1304" data-mp="2bp3mcv8gll93">
            <ChevronLeftIcon className="h-4 w-4" data-id="element-1305" data-mp="m9nd7fujbzj7" />
          </button>
          <span className="px-3 text-sm font-semibold text-navy" aria-live="polite" data-id="element-1306" data-mp="mc04wkq3ho9g">
            {year}
          </span>
          <button type="button" onClick={() => setYear(y => y + 1)} disabled={year >= maxYear} aria-label="Next year" className="flex h-8 w-8 items-center justify-center rounded-lg text-navy transition hover:bg-canvas disabled:opacity-30" data-id="element-1307" data-mp="a07h5sb2uqej">
            <ChevronRightIcon className="h-4 w-4" data-id="element-1308" data-mp="xq1qw103nyuk" />
          </button>
        </div>
        <span className="text-sm text-muted" data-id="element-1309" data-mp="sqowmv6wfphy">
          <span className="font-semibold text-navy" data-id="element-1310" data-mp="sqyo6n7le4pr">{yearHolidays.length}</span> holidays · {companyCount} company {companyCount === 1 ? 'day' : 'days'} · {halfCount} half{' '}
          {halfCount === 1 ? 'day' : 'days'}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-6 2xl:grid-cols-[minmax(0,1fr)_420px]" data-id="element-1311" data-mp="38hl3twezyeie">
        <Card title={`${year} calendar`} icon={CalendarDaysIcon} data-id="element-1312" data-mp="38f8h75yanfz0">
          <YearCalendar year={year} holidays={yearHolidays} onSelect={isAdmin ? h => openForm(h) : undefined} data-id="element-1313" data-mp="34eesiad11x52" />
        </Card>

        <Card title="Holiday list" subtitle={isAdmin ? 'Click the calendar or edit below' : undefined} icon={ListIcon} bodyClassName="px-3 pb-3" data-id="element-1314" data-mp="w5gqltjd6gvp">
          {yearHolidays.length === 0 ? <div className="px-3 pb-3" data-id="element-1315" data-mp="m70ypocij3l5">
              <EmptyState icon={CalendarDaysIcon} title={`No holidays set for ${year}`} action={isAdmin && <Button size="sm" variant="primary" onClick={() => openForm(null)} data-id="element-1317" data-mp="1jwuftqm7xwcg">
                      Add holiday
                    </Button>} data-id="element-1316" data-mp="r7nmd61mfwy2" />
            </div> : <ul className="space-y-0.5" data-id="element-1318" data-mp="19xn5lcas6htk">
              {yearHolidays.map(h => {
            const past = h.date < TODAY_ISO;
            return <li key={h.id} className={`group flex items-center gap-3 rounded-xl px-3 py-2.5 transition hover:bg-canvas/70 ${past ? 'opacity-55' : ''}`} data-id="element-1319" data-mp="3uoxdfai5hei0">
                    <div className={`flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-xl ${h.kind === 'Company' ? 'bg-flame/10 text-[#c25a00]' : 'bg-navy/[0.05] text-navy'}`} data-id="element-1320" data-mp="3n3tjw5675rpx">
                      <span className="text-[10px] font-semibold uppercase leading-none" data-id="element-1321" data-mp="xo8jjnwc3e1">{format(parseISO(h.date), 'MMM')}</span>
                      <span className="text-base font-semibold leading-tight" data-id="element-1322" data-mp="3s2my5ztb5mr5">{format(parseISO(h.date), 'd')}</span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-1323" data-mp="2zglezocokkof">
                      <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium text-navy" data-id="element-1324" data-mp="2zdpim4ghe423">
                        <span className="truncate" data-id="element-1325" data-mp="3e6j90kxak4lj">{h.name}</span>
                        <Badge tone={h.kind === 'Company' ? 'orange' : 'navy'} data-id="element-1326" data-mp="39cezpv0jzkha">{h.kind}</Badge>
                        {h.halfDay && <Badge tone="amber" data-id="element-1327" data-mp="2lmko1grz01ym">Half day</Badge>}
                      </p>
                      <p className="truncate text-xs text-muted" data-id="element-1328" data-mp="2goj1glvc5kie">
                        {format(parseISO(h.date), 'EEEE')} · {appliesToLabel(h, policyName)}
                      </p>
                    </div>
                    {isAdmin && <div className="flex shrink-0 gap-0.5" data-id="element-1329" data-mp="3f5rrpjwqizn4">
                        <button type="button" onClick={() => openForm(h)} aria-label={`Edit ${h.name}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition hover:bg-white hover:text-navy" data-id="element-1330" data-mp="3f6jqt4pjjfi5">
                          <PencilIcon className="h-4 w-4" data-id="element-1331" data-mp="2ngyii64r2mn1" />
                        </button>
                        <button type="button" onClick={() => setDeleting(h)} aria-label={`Delete ${h.name}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition hover:bg-rose-50 hover:text-rose-600" data-id="element-1332" data-mp="2ri9zjn9k5zjm">
                          <Trash2Icon className="h-4 w-4" data-id="element-1333" data-mp="bpkplhtyxagg" />
                        </button>
                      </div>}
                  </li>;
          })}
            </ul>}
          <p className="mx-3 mt-3 flex items-start gap-2 rounded-xl bg-azure/[0.06] px-3 py-2.5 text-xs text-slate-600" data-id="element-1334" data-mp="21557xhsnmqj6">
            <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-azure" aria-hidden="true" data-id="element-1335" data-mp="1w7lqk90ntxn0" />
            Full holidays aren't counted in time-off requests; half days count as 0.5.
          </p>
        </Card>
      </div>

      {isAdmin && <>
          <HolidayFormModal open={formOpen} holiday={editing} defaultYear={year} onClose={() => setFormOpen(false)} data-id="element-1336" data-mp="1ig8d9ctchw0f" />
          <Modal open={deleting !== null} onClose={() => setDeleting(null)} title="Delete holiday?" description={deleting ? `${deleting.name} · ${format(parseISO(deleting.date), 'MMM d, yyyy')}` : undefined} footer={<>
                <Button onClick={() => setDeleting(null)} data-id="element-1338" data-mp="32uhb4j5x40ew">Cancel</Button>
                <Button variant="danger" icon={Trash2Icon} onClick={() => {
          if (deleting) hrActions.deleteHoliday(deleting.id);
          toast('Holiday deleted', {
            description: `${deleting?.name} will now count as a regular workday.`
          });
          setDeleting(null);
        }} data-id="element-1339" data-mp="2xwpb11v4d7zm">
                  Delete
                </Button>
              </>} data-id="element-1337" data-mp="1dfkm8r549f67">
            <p className="text-sm text-slate-600" data-id="element-1340" data-mp="1o0lt1oulw0kh">Time-off requests on this date will start counting it as a business day.</p>
          </Modal>
        </>}
    </div>;
}